import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Star } from "lucide-react";
import SEO from "@/components/SEO";

const TestimonialsPage = ({ seoSettings }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 lg:pt-[118px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading testimonials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-[118px] min-h-screen">
      <SEO 
        title={`Testimonials - ${seoSettings?.seo_title || 'Mélange Mobile Bar'}`}
        description={seoSettings?.seo_description}
        keywords={seoSettings?.seo_keywords}
        ogImage={seoSettings?.seo_og_image}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F7F4F0] to-[#F7F4F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="testimonials-page-title">
            Client Testimonials
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Read what our satisfied clients have to say about their experience
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                data-testid={`testimonial-item-${index}`}
                className="bg-white p-8 rounded-lg shadow-lg hover-lift"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="#d97706" className="text-[#8B7355]" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 text-sm italic leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900 mb-1">{testimonial.client_name}</p>
                  {testimonial.event_type && (
                    <p className="text-sm text-gray-600">{testimonial.event_type}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {testimonials.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-600">No testimonials available yet.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
